import { CurrencyPipe, DatePipe, PercentPipe } from '@angular/common';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButton, MatAnchor } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ApexChart,
  ApexDataLabels,
  ApexNonAxisChartSeries,
  ApexOptions,
  ApexPlotOptions,
  ApexTooltip,
  ChartComponent,
} from 'ng-apexcharts';
import dayjs from 'dayjs';
import { firstValueFrom } from 'rxjs';
import { WorkingContract } from 'src/app/models/working_contract.model';
import { ApiService } from 'src/app/services/api.service';
import { UserService } from 'src/app/services/user.service';
import { HtmlDialogComponent } from '../../../../shared/html-dialog/html-dialog.component';

interface CustomApexOptions extends ApexOptions {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  colors: string[];
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  tooltip: ApexTooltip;
}

@Component({
  selector: 'app-my-remuneration',
  templateUrl: './my-remuneration.component.html',
  styleUrls: ['./my-remuneration.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ChartComponent, MatButton, MatAnchor, MatIcon, RouterLink, MatTooltip, PercentPipe, CurrencyPipe, DatePipe],
})
export class MyRemunerationComponent implements OnInit {
  private api = inject(ApiService);
  private user = inject(UserService);
  private dialog = inject(MatDialog);
  private snack = inject(MatSnackBar);
  private sanitizer = inject(DomSanitizer);
  private currency = inject(CurrencyPipe);

  contract: WorkingContract | null = null;
  @ViewChild('chart') chart: ChartComponent;
  paqueteRetributivoChartOptions: CustomApexOptions;
  next_upgrade:
    | undefined
    | { days_remaining: number; salary: number; health_insurance: boolean; ret_flexible: number; date: Date; category_name: string };

  ngOnInit(): void {
    this.fetchActiveContract();
    this.fetchActiveContractNextSalarialUpgrade();
  }

  seeHealthInsuranceCard(): void {
    const card =
      this.contract?.current_working_contract_variable_condition.health_insurance_contract?.active_health_insurance_contract_card;

    if (!card) {
      this.snack.open('No se encontró la tarjeta de póliza.', 'Ok', { duration: 3000 });
      return;
    }

    const docs = [card.front_card_document, card.back_card_document].filter((doc): doc is NonNullable<typeof doc> => !!doc?.id);
    if (docs.length === 0) {
      this.snack.open('No hay documentos de la tarjeta de póliza disponibles.', 'Ok', { duration: 3000 });
      return;
    }

    const promises: Promise<Blob>[] = docs.map(doc => firstValueFrom(this.api.downloadDocument(doc.id)));

    Promise.all(promises)
      .then((blobs: Blob[]) => {
        const urls: string[] = blobs.map((blob: Blob) => window.URL.createObjectURL(blob));
        const html: string = blobs
          .map(
            (_: Blob, index: number): string =>
              `<p>${index ? 'Trasera' : 'Frontal'}: <img src="${urls[index]}" style="display: block; width: 100%; max-width: 250px;" /></p>`
          )
          .join('');

        this.dialog
          .open(HtmlDialogComponent, {
            width: '600px',
            data: {
              dialog_close_button: 'Cerrar',
              title: 'Mi tarjeta de póliza',
              html: this.sanitizer.bypassSecurityTrustHtml(html),
            },
          })
          .afterClosed()
          .subscribe((): void => {
            urls.forEach((url: string): void => window.URL.revokeObjectURL(url));
          });
      })
      .catch((): void => {
        this.snack.open('Error al cargar las imágenes de la tarjeta de póliza.', 'Ok', { duration: 3000 });
      });
  }

  openRetribucionFlexible(): void {
    this.dialog.open(HtmlDialogComponent, {
      width: '600px',
      data: {
        dialog_close_button: 'Cerrar',
        title: '¿Como usar la Ret. flexible?',
        html: this.sanitizer.bypassSecurityTrustHtml(`
          <p>
            <b>¿Cómo funciona la tarjeta de comida de Ret. flexible?</b><br>
            Recibirás una tarjeta que podrás utilizar en restaurantes y cafeterías, con un saldo asignado para gastar cada mes. Este saldo estará disponible desde el día 1 hasta el día 27 de cada mes.
          </p>
          <p>
            <b>¿Cómo se recarga el saldo?</b><br>
            El saldo se recargará diariamente con un máximo de 18 euros por día, hasta que alcances el límite mensual que te corresponda. Veamos un ejemplo:
          </p>
          <p>
            <b>Si tu límite mensual es de 50 €:</b><br>
            <ul>
              <li><b>Día 1</b>: No recibirás saldo.</li>
              <li><b>Día 2</b>: Se te añadirán 18 €.</li>
              <li><b>Día 3</b>: Se te añadirán otros 18 €, acumulando un total de 36 €.</li>
              <li><b>Día 4</b>: Se te añadirán 14 €, alcanzando tu límite de 50 €.</li>
              <li><b>Día 5 y posteriores</b>: No recibirás más saldo, ya que habrás alcanzado el tope del mes.</li>
            </ul>
          </p>
          <p>
            <b>¿Qué sucede si no gastas todo el saldo?</b><br>
            El saldo que no utilices en la tarjeta de comida no se perderá. Cualquier cantidad que quede sin gastar al final del mes se sumará a tu nómina como un "extra". Sin embargo, este importe adicional tributará como parte de tu salario en el IRPF, lo que significa que estará sujeto a las retenciones fiscales correspondientes.
          </p>
        `),
      },
    });
  }

  private fetchActiveContract(): void {
    this.api
      .getActiveWorkingContract(this.user.getCurrentUser().id, {
        refresh_cache: true,
        with: 'current_working_contract_variable_condition..health_insurance_contract..health_insurance_plan..health_insurance_company,current_working_contract_variable_condition..health_insurance_contract..active_health_insurance_contract_variable_condition,current_working_contract_variable_condition..health_insurance_contract..active_health_insurance_contract_card..front_card_document,current_working_contract_variable_condition..health_insurance_contract..active_health_insurance_contract_card..back_card_document,current_working_contract_variable_condition..department_categories',
      })
      .subscribe(data => {
        this.contract = data;
        this.mountChart();
      });
  }

  private mountChart(): void {
    if (!this.contract) {
      return;
    }

    const self = this;
    const series = [this.contract.current_working_contract_variable_condition.brute_anual_bisual_salary / 12];
    const labels = ['Salario base'];
    const colors = ['#008FFB'];

    if (this.contract.current_working_contract_variable_condition.current_triennium_anual_salary > 0) {
      series.push(this.contract.current_working_contract_variable_condition.current_triennium_anual_salary / 12);
      labels.push('Plus Antigüedad');
      colors.push('#E7E3D6');
    }

    if (this.contract.current_working_contract_variable_condition.health_insurance_contract != null) {
      series.push(
        this.contract.current_working_contract_variable_condition.health_insurance_contract
          .active_health_insurance_contract_variable_condition.monthly_price
      );
      labels.push('Seguro médico');
      colors.push('#D62787');
    }

    if (
      this.contract.current_working_contract_variable_condition.extract_flexible_remuneration_from_salary &&
      this.contract.current_working_contract_variable_condition.flexible_remuneration_monthly_quantity != null &&
      this.contract.current_working_contract_variable_condition.flexible_remuneration_monthly_quantity > 0
    ) {
      series.push(this.contract.current_working_contract_variable_condition.flexible_remuneration_monthly_quantity);
      labels.push('Tarjeta Ret. flexible');
      colors.push('#00E396');
    }

    const sum = series.reduce((prev, curr) => prev + curr);

    this.paqueteRetributivoChartOptions = {
      series,
      chart: {
        type: 'donut',
        width: '100%',
        height: 280,
      },
      labels,
      colors,
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: function () {
                  return self.currency.transform(sum) as string;
                },
                fontSize: '12px',
                fontWeight: 'bold',
                showAlways: true,
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return self.currency.transform(val) as string;
          },
        },
      },
    };
  }

  private fetchActiveContractNextSalarialUpgrade(): void {
    this.api.getActiveWorkingContractNextSalarialUpgrade(this.user.getCurrentUser().id, { with: '' }).subscribe(data => {
      if (data.data) {
        this.next_upgrade = {
          date: data.data.start_date,
          days_remaining: -dayjs().diff(dayjs(data.data.start_date), 'days'),
          salary: data.data.brute_anual_bisual_salary + data.data.current_triennium_anual_salary,
          health_insurance: data.data.health_insurance_contract_id != null,
          ret_flexible: data.data.flexible_remuneration_monthly_quantity ?? 0,
          category_name: data.data.department_categories.map(dc => dc.name).join(','),
        };
      }
    });
  }
}
